import { useState, useEffect } from "react";
import { Eye, EyeOff, Pencil, Plus, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const PAGE_SIZE = 10;
import { useQueryClient } from "@tanstack/react-query";
import { useUsers, useCreateUser, useAdminUpdateUser } from "@/hooks/useUsers";
import { usersApi } from "@/services/usersApi";
import type { ApiUser } from "@/services/usersApi";

const ROLES = ["User", "Admin"] as const;

/** Normalize legacy DB values (e.g. "GVD Writer") to "User" */
function normalizeRole(role: string): "User" | "Admin" {
  return role === "Admin" ? "Admin" : "User";
}

interface AddUserForm {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  temporary_password: string;
}

const emptyForm = (): AddUserForm => ({
  email: "",
  first_name: "",
  last_name: "",
  role: "User",
  temporary_password: "",
});

const Users = () => {
  const queryClient = useQueryClient();
  const { data: users = [], isLoading } = useUsers();
  const createUser = useCreateUser();
  const adminUpdate = useAdminUpdateUser();

  // Pagination
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = users.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getPageNumbers = (): (number | "ellipsis")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | "ellipsis")[] = [1];
    if (currentPage > 3) pages.push("ellipsis");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages);
    return pages;
  };

  // Sync roles from Cognito groups once on page mount
  useEffect(() => {
    usersApi.syncRoles().then(() => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add user dialog
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<AddUserForm>(emptyForm());
  const [showPassword, setShowPassword] = useState(false);
  const [createdTempPassword, setCreatedTempPassword] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // Edit user dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<ApiUser | null>(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", role: "" });
  const [editError, setEditError] = useState<string | null>(null);

  const openAdd = () => {
    setForm(emptyForm());
    setShowPassword(false);
    setCreatedTempPassword(null);
    setAddError(null);
    setAddOpen(true);
  };

  const handleAdd = async () => {
    setAddError(null);
    try {
      await createUser.mutateAsync(form);
      setCreatedTempPassword(form.temporary_password);
      setForm(emptyForm());
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to create user";
      setAddError(msg);
    }
  };

  const closeAdd = () => {
    setAddOpen(false);
    setCreatedTempPassword(null);
  };

  const openEdit = (user: ApiUser) => {
    setEditUser(user);
    setEditForm({ first_name: user.first_name, last_name: user.last_name, role: normalizeRole(user.role) });
    setEditError(null);
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setEditError(null);
    try {
      await adminUpdate.mutateAsync({ userId: editUser.id, payload: editForm });
      setEditOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to update user";
      setEditError(msg);
    }
  };

  const handleToggleActive = (user: ApiUser) => {
    adminUpdate.mutate({ userId: user.id, payload: { is_active: !user.is_active } });
  };

  const isAddFormValid =
    form.email.trim() &&
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.temporary_password.trim();

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={openAdd} className="gap-1 bg-brand-teal text-white hover:bg-brand-teal/90">
          <Plus className="w-4 h-4" /> Add User
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-28">Role</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No users found.
                </TableCell>
              </TableRow>
            )}
            {paginated.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  {user.first_name} {user.last_name}
                </TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  <Badge variant={normalizeRole(user.role) === "Admin" ? "default" : "secondary"}>
                    {normalizeRole(user.role)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={user.is_active ? "default" : "secondary"}>
                    {user.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(user)} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleToggleActive(user)}
                      title={user.is_active ? "Deactivate" : "Activate"}
                    >
                      {user.is_active
                        ? <ToggleRight className="w-4 h-4 text-brand-teal" />
                        : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-disabled={currentPage === 1}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {getPageNumbers().map((p, i) =>
                p === "ellipsis" ? (
                  <PaginationItem key={`ellipsis-${i === 1 ? "start" : "end"}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === currentPage}
                      onClick={() => setPage(p)}
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-disabled={currentPage === totalPages}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { if (!open) closeAdd(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>

          {createdTempPassword ? (
            <div className="py-2 space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                <p className="font-semibold mb-1">User created successfully.</p>
                <p>Share the temporary password with the user. They will be prompted to set a new password on first login.</p>
              </div>
              <div>
                <Label className="font-semibold">Temporary Password</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    readOnly
                    value={createdTempPassword}
                    type={showPassword ? "text" : "password"}
                    className="flex-1 font-mono"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button className="bg-brand-teal text-white hover:bg-brand-teal/90" onClick={closeAdd}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-2 space-y-4">
              {addError && (
                <div className="rounded-md bg-destructive/10 text-destructive text-sm p-3">
                  {addError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">First Name</Label>
                  <Input
                    className="mt-1.5"
                    value={form.first_name}
                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="font-semibold">Last Name</Label>
                  <Input
                    className="mt-1.5"
                    value={form.last_name}
                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Email</Label>
                <Input
                  className="mt-1.5"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <Label className="font-semibold">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Temporary Password</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    className="flex-1"
                    type={showPassword ? "text" : "password"}
                    value={form.temporary_password}
                    onChange={(e) => setForm((f) => ({ ...f, temporary_password: e.target.value }))}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeAdd}>Cancel</Button>
                <Button
                  className="bg-brand-teal text-white hover:bg-brand-teal/90"
                  onClick={handleAdd}
                  disabled={createUser.isPending || !isAddFormValid}
                >
                  {createUser.isPending ? "Creating…" : "Create User"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {editError && (
              <div className="rounded-md bg-destructive/10 text-destructive text-sm p-3">
                {editError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">First Name</Label>
                <Input
                  className="mt-1.5"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div>
                <Label className="font-semibold">Last Name</Label>
                <Input
                  className="mt-1.5"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              className="bg-brand-teal text-white hover:bg-brand-teal/90"
              onClick={handleEdit}
              disabled={adminUpdate.isPending}
            >
              {adminUpdate.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Users;
